/**
 * Portál Hearth & Table — titulná stránka tabletop.dustbox.app. Účet patrí
 * portálu, hry sú pod ním. Portál **o hrách nič nevie**: zoznam aj dlaždice
 * dodáva register hier (`games/registry.ts`), každá hra žije vo vlastnom
 * priečinku `games/<hra>/` a pod vlastnou cestou `#/<hra>/…`.
 *
 * Rozloženie: knižnica hier vľavo, bokom patch notes a ako sa do herne dostať.
 * Z predlohy je zámerne vynechané „kto je online" a chat — potrebovali by
 * nové služby a menili by súkromie (viď CLAUDE.md).
 */

import { GAMES } from '../games/registry.js';
import { ChangelogPreview } from './Changelog.js';
import { DieIcon } from './GameCard.js';

interface Props {
  nick: string;
  navigate: (hash: string) => void;
}

/** „2 hry", „5 hier" — slovenský tvar podľa počtu. */
function gamesLabel(count: number): string {
  if (count === 1) return '1 hra';
  if (count >= 2 && count <= 4) return `${count} hry`;
  return `${count} hier`;
}

/** Čo ďalej — krátkodobo (rozšírenia hier, ktoré už bežia), dlhodobo (nové hry) a naveky. */
const PLANS: { title: string; items: { game: string; what?: string }[] }[] = [
  {
    title: 'Krátkodobo',
    items: [
      { game: 'Unmatched', what: 'ďalšie postavy' },
      { game: 'Dune', what: 'rozšírenia (DLC)' },
      { game: 'Star Wars: Outer Rim', what: 'doladenie (animácie, boti do voľných miest)' },
    ],
  },
  {
    title: 'Dlhodobo',
    items: [
      { game: 'Scythe', what: 'nová hra' },
    ],
  },
  {
    title: 'Never-ending',
    items: [{ game: 'Bug-fixing :)' }],
  },
];

export function Portal({ nick, navigate }: Props) {
  return (
    <div className="portal">
      <div className="portal__layout">
        <section className="library">
          <header className="library__head">
            <h1 className="library__title">
              <DieIcon /> Naša knižnica hier
            </h1>
            <span className="library__count">{gamesLabel(GAMES.length)} na polici</span>
          </header>
          <p className="library__hello">
            Ahoj, <b>{nick}</b> — prisadni si k ohňu a vyber, čo sa dnes hrá.
          </p>

          <div className="library__grid">
            {GAMES.map(({ id, Tile }) => (
              <Tile key={id} navigate={navigate} />
            ))}
          </div>
        </section>

        <aside className="portal__side">
          <ChangelogPreview onOpen={(scope) => navigate(scope ? `#/patch-notes/${scope}` : '#/patch-notes')} />
          <section className="panel portalNote portalPlans">
            <header className="panel__head">
              <h2 className="panel__title">Plány</h2>
            </header>
            {PLANS.map((group) => (
              <div key={group.title} className="portalPlans__group">
                <h3 className="portalPlans__title">{group.title}</h3>
                <ul>
                  {group.items.map((item) => (
                    <li key={item.game}>
                      <b>{item.game}</b>
                      {item.what ? ` — ${item.what}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
          <section className="panel portalNote">
            <header className="panel__head">
              <h2 className="panel__title">Súkromná herňa</h2>
            </header>
            <p>
              Hráme len medzi sebou. Nový hráč sa zaregistruje s pozývacím kódom, potom už len
              menom a heslom — jeden účet na všetky hry.
            </p>
            <p>
              Do rozohranej hry pozveš kamaráta odkazom na stôl. Kto odkaz nemá, k stolu sa
              nedostane.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}
